import React, { useState, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import PlayerImg from "./PlayerImg";

export default function KboCandlestickChart({ kboData }) {
    const [selectedMetric, setSelectedMetric] = useState("ops");
    const [timeframe, setTimeframe] = useState("daily"); // daily, weekly, monthly
    const [chartSeries, setChartSeries] = useState([]);

    // 스탯별 기본 Y축 범위 세팅
    const defaultYLimits = {
        avg: { min: 0.15, max: 0.4 },
        obp: { min: 0.25, max: 0.5 },
        slg: { min: 0.3, max: 0.7 },
        ops: { min: 0.5, max: 1.2 },
        eff_ops: { min: 0.5, max: 1.2 },
    };

    const [yMin, setYMin] = useState(defaultYLimits["ops"].min);
    const [yMax, setYMax] = useState(defaultYLimits["ops"].max);
    const [hideEmptyDates, setHideEmptyDates] = useState(false);

    // 💡 탭(selectedMetric)을 누를 때마다 Y축을 즉각 재계산하는 엔진 (여기에 다시 삽입)
    useEffect(() => {
        if (
            kboData &&
            kboData.success &&
            Array.isArray(kboData.data) &&
            kboData.data.length > 0
        ) {
            const highValues = kboData.data.map((item) =>
                Number(item[selectedMetric]?.high || 0),
            );
            // 최저가 탐색 시 0으로 바닥이 뚫리는 것을 막기 위한 초기값 100 세팅
            const lowValues = kboData.data.map((item) =>
                Number(item[selectedMetric]?.low || 100),
            );

            const dataMax = Math.max(...highValues);
            const dataMin = Math.min(...lowValues);

            const diff = dataMax - dataMin;
            const padding = diff === 0 ? 0.05 : diff * 0.2; // 상하 20% 여백 생성

            setYMin(Math.max(0, dataMin - padding).toFixed(3));
            setYMax(dataMax.toFixed(3));
        } else {
            // 데이터가 비었을 때 기본값으로 회귀
            setYMin(defaultYLimits[selectedMetric]?.min || 0);
            setYMax(defaultYLimits[selectedMetric]?.max || 1);
        }
    }, [kboData, selectedMetric]);

    // 💡 일봉/주봉/월봉 OHLC 병합 및 7이평, 30이평 생성 알고리즘
    useEffect(() => {
        if (
            kboData &&
            kboData.success &&
            Array.isArray(kboData.data) &&
            kboData.data.length > 0
        ) {
            // 1. 그룹화하기 전, '순수 일별 데이터' 상태에서 7일/30일 이평선을 먼저 무자비하게 계산한다.
            const dailyDataWithMA = kboData.data.map((item, index, arr) => {
                let ma7 = null;
                if (index >= 6) {
                    let sum = 0;
                    for (let i = 0; i < 7; i++)
                        sum += Number(
                            arr[index - i][selectedMetric]?.close || 0,
                        );
                    ma7 = Number((sum / 7).toFixed(3));
                }

                let ma30 = null;
                if (index >= 29) {
                    let sum = 0;
                    for (let i = 0; i < 30; i++)
                        sum += Number(
                            arr[index - i][selectedMetric]?.close || 0,
                        );
                    ma30 = Number((sum / 30).toFixed(3));
                }

                return { ...item, ma7, ma30 };
            });

            // 2. 이평선이 장착된 데이터를 바탕으로 주봉/월봉 그룹화를 진행한다.
            const grouped = {};

            dailyDataWithMA.forEach((item) => {
                let groupKey = item.date;

                if (timeframe === "weekly") {
                    groupKey = getStartOfWeek(item.date);
                } else if (timeframe === "monthly") {
                    groupKey = getStartOfMonth(item.date);
                }

                const metricData = item[selectedMetric] || {};
                const o = Number(metricData.open || 0);
                const h = Number(metricData.high || 0);
                const l = Number(metricData.low || 0);
                const c = Number(metricData.close || 0);

                if (!grouped[groupKey]) {
                    // 첫 날짜 세팅 시 이평선 값도 저장
                    grouped[groupKey] = {
                        date: groupKey,
                        open: o,
                        high: h,
                        low: l,
                        close: c,
                        ma7: item.ma7,
                        ma30: item.ma30,
                    };
                } else {
                    grouped[groupKey].high = Math.max(
                        grouped[groupKey].high,
                        h,
                    );
                    grouped[groupKey].low = Math.min(grouped[groupKey].low, l);
                    grouped[groupKey].close = c;

                    // 💡 핵심: 기간 내 데이터가 누적될 때마다, 마지막 날짜의 '일간 이평선' 값으로 계속 덮어씌움
                    grouped[groupKey].ma7 = item.ma7;
                    grouped[groupKey].ma30 = item.ma30;
                }
            });

            // 3. 차트 배열 포맷으로 변환하며 캔들과 이평선 시리즈를 깔끔하게 분리
            const mappedCandle = [];
            const mappedMA7 = [];
            const mappedMA30 = [];

            Object.values(grouped).forEach((g) => {
                const xVal = hideEmptyDates
                    ? g.date
                    : new Date(
                          g.date.length === 7
                              ? `${g.date}-01T00:00:00`
                              : `${g.date}T00:00:00`,
                      ).getTime();

                mappedCandle.push({
                    x: xVal,
                    y: [g.open, g.high, g.low, g.close],
                });
                mappedMA7.push({ x: xVal, y: g.ma7 });
                mappedMA30.push({ x: xVal, y: g.ma30 });
            });

            // 4. 최종 시리즈 등록
            setChartSeries([
                { name: "기본", type: "candlestick", data: mappedCandle },
                { name: "7-이평선", type: "line", data: mappedMA7 },
                { name: "30-이평선", type: "line", data: mappedMA30 },
            ]);
        } else {
            setChartSeries([]);
        }
    }, [kboData, selectedMetric, timeframe, hideEmptyDates]);

    const tabs = [
        { id: "avg", name: "타율" },
        { id: "obp", name: "출루율" },
        { id: "slg", name: "장타율" },
        { id: "ops", name: "OPS" },
        { id: "eff_ops", name: "실질 OPS" },
    ];

    // 💡 날짜 그룹화 헬퍼 함수
    const getStartOfWeek = (dateStr) => {
        const d = new Date(`${dateStr}T00:00:00`);
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1); // 월요일 기준
        const monday = new Date(d.setDate(diff));
        return `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, "0")}-${String(monday.getDate()).padStart(2, "0")}`;
    };

    const getStartOfMonth = (dateStr) => {
        return dateStr.substring(0, 7); // YYYY-MM
    };

    const hasData =
        kboData &&
        kboData.success &&
        Array.isArray(kboData.data) &&
        kboData.data.length > 0;
    const playerName = kboData?.name || "선수명";
    const playerId = kboData?.player_id || "";

    let currentPrice = 0;
    let allTimeHigh = 0;
    let priceChange = 0;
    let priceChangePercent = 0;
    let changeStatus = "neutral"; // 상승(up), 하락(down), 보합(neutral)
    let metricName = tabs.find((t) => t.id === selectedMetric)?.name || "";

    if (hasData) {
        const dataLen = kboData.data.length;
        const lastItem = kboData.data[dataLen - 1];
        currentPrice = Number(lastItem[selectedMetric]?.close || 0);
        allTimeHigh = Math.max(
            ...kboData.data.map((item) =>
                Number(item[selectedMetric]?.high || 0),
            ),
        );

        // 직전 캔들이 존재할 경우 등락률 폭풍 계산
        if (dataLen > 1) {
            const prevPrice = Number(
                kboData.data[dataLen - 2][selectedMetric]?.close || 0,
            );
            priceChange = currentPrice - prevPrice;
            priceChangePercent =
                prevPrice !== 0 ? (priceChange / prevPrice) * 100 : 0;

            if (priceChange > 0) changeStatus = "up";
            else if (priceChange < 0) changeStatus = "down";
        }
    }

    const options = {
        chart: {
            type: "line", // 혼합 차트를 그리기 위해 베이스를 line으로 둔다
            height: 500,
            fontFamily: "inherit",
            toolbar: {
                show: true, // 툴바 표시
                tools: {
                    download: true, // PNG/SVG/CSV 다운로드 버튼
                    selection: true, // 선택 도구
                    zoom: true, // 돋보기 (드래그로 범위 설정)
                    zoomin: true, // + 확대 버튼
                    zoomout: true, // - 축소 버튼
                    pan: true, // 손바닥 (드래그로 좌우 이동)
                    reset: true, // 원래 배율로 초기화
                },
                // 💡 핵심: 모바일/PC 모두 기본 드래그 동작을 '좌우 이동(pan)'으로 고정합니다.
                autoSelected: "pan",
            },
            // 💡 애니메이션 폭발적 활성화
            animations: {
                enabled: true,
                easing: "easeinout",
                speed: 800,
                dynamicAnimation: { enabled: true, speed: 350 },
            },
        },
        colors: ["#000", "#10b981", "#8b5cf6"], // [캔들용(무시됨), 이평선 에메랄드, 보라색]
        stroke: {
            width: [1, 1, 1],
            curve: "smooth",
        },
        xaxis: {
            type: "datetime", // category 변환이나 복잡한 우회 로직 없이 고정
            labels: {
                datetimeUTC: false,
                format: timeframe === "monthly" ? "yyyy/MM" : "MM/dd",
            },
            tooltip: { enabled: false },
        },
        yaxis: {
            min: yMin !== "" && !isNaN(yMin) ? Number(yMin) : undefined,
            max: yMax !== "" && !isNaN(yMax) ? Number(yMax) : undefined,
            tooltip: { enabled: false }, // Y축 자체 툴팁은 끄고 커스텀 툴팁으로 일원화
            labels: {
                formatter: (value) =>
                    typeof value === "number" ? value.toFixed(3) : value,
            },
        },
        plotOptions: {
            candlestick: {
                colors: { upward: "#ef4444", downward: "#3b82f6" },
                wick: { useFillColor: true },
            },
        },
        grid: { borderColor: "#f3f4f6", strokeDashArray: 4 },
        // 💡 날짜 표기 + 한글화된 커스텀 HTML 툴팁 강제 주입
        tooltip: {
            shared: true,
            intersect: false,
            custom: function ({ seriesIndex, dataPointIndex, w }) {
                const candleData =
                    w.globals.initialSeries[0]?.data?.[dataPointIndex];

                if (!candleData) return "";

                // 날짜 포맷 (예: 2025년 3월 28일)
                const d = new Date(candleData.x);
                const dateStr = `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일`;

                const o = candleData.y[0].toFixed(3);
                const h = candleData.y[1].toFixed(3);
                const l = candleData.y[2].toFixed(3);
                const c = candleData.y[3].toFixed(3);

                return `
          <div class="p-4 bg-white/95 backdrop-blur-sm border border-gray-200 rounded-xl shadow-2xl font-sans min-w-[150px]">
            <div class="text-xs font-black text-gray-500 mb-3 pb-2 border-b border-gray-100 text-center tracking-tight">${dateStr}</div>
            <div class="flex flex-col gap-1.5 text-sm">
              <div class="flex justify-between gap-5"><span class="text-gray-500 font-bold">시작</span><span class="font-bold text-gray-900">${o}</span></div>
              <div class="flex justify-between gap-5"><span class="text-gray-500 font-bold">최고</span><span class="font-black text-red-500">${h}</span></div>
              <div class="flex justify-between gap-5"><span class="text-gray-500 font-bold">최저</span><span class="font-black text-blue-500">${l}</span></div>
              <div class="flex justify-between gap-5"><span class="text-gray-500 font-bold">마지막</span><span class="font-bold text-gray-900">${c}</span></div>
            </div>
          </div>
        `;
            },
        },
    };

    return (
        <div className="w-full max-w-5xl mx-auto p-4 bg-white rounded-2xl shadow-xl border border-gray-100 mt-8 font-family-NaSqNe">
            {/* 상단: 주식 HTS 감성의 종목명 & 현재가 전광판 UI */}
            {hasData && (
                <div className="flex items-center justify-between bg-gray-50 px-5 py-3 rounded-xl mb-6 border border-gray-200">
                    <div className="flex items-center gap-2 md:gap-4">
                        <div className="w-12 h-12 md:w-16 md:h-16 shrink-0 rounded-full bg-white overflow-hidden border-2 border-gray-300 shadow-sm flex items-center justify-center">
                            <PlayerImg
                                p_no={playerId}
                                p_img={kboData?.img || ""}
                            />
                        </div>
                        <div>
                            <div className="text-sm font-bold text-gray-500 px-1 mb-1 bg-gray-200 rounded-md">
                                <a
                                    href={`https://www.koreabaseball.com/Record/Player/HitterDetail/Basic.aspx?playerId=${playerId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    #{playerId}
                                </a>
                            </div>
                            <div className="text-xl font-black text-gray-900 tracking-tight">
                                {playerName}
                            </div>
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-sm font-bold text-gray-500 mb-1 flex items-center justify-end gap-1.5">
                            <span>
                                {kboData.year} {metricName}
                            </span>

                            {/* 💡 실질 OPS 등 지표 설명을 위한 물음표 툴팁 아이콘 */}
                            {selectedMetric === "eff_ops" && (
                                <div className="relative group inline-flex items-center justify-center">
                                    <span className="w-4 h-4 text-xs font-bold text-gray-400 bg-gray-200 rounded-full flex items-center justify-center cursor-help hover:bg-gray-300 hover:text-gray-600 transition-colors">
                                        ?
                                    </span>

                                    {/* 💡 호버 시 나타나는 커스텀 툴팁 박스 */}
                                    <div className="absolute right-0 bottom-full mb-2 hidden group-hover:block w-56 p-3 bg-gray-900 text-white text-xs font-medium rounded-lg shadow-xl z-50 text-left leading-relaxed">
                                        <>
                                            <p className="font-bold text-emerald-400 mb-1">
                                                실질 OPS란?
                                            </p>
                                            - 도루를 OPS에 반영한 가상
                                            지표입니다.<br></br>- 출루 후 도루
                                            성공 또는 실패 시 총 루타 수에
                                            반영되고, 아웃 출루 후 도루 성공 시
                                            희생번트로 인정합니다.<br></br>-
                                            실제로 통용되는 기록은 아니니 재미로
                                            봐주세요!
                                        </>
                                        {/* 말풍선 꼬리 */}
                                        <div className="absolute top-full right-2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="text-3xl font-black text-red-500 tracking-tighter mb-1">
                            {currentPrice.toFixed(3)}
                        </div>
                        {/* 💡 전일 대비 등락폭 및 등락률 UI 추가 */}
                        {hasData && kboData.data.length > 1 && (
                            <div
                                className={`text-sm font-bold ${
                                    changeStatus === "up"
                                        ? "text-red-500"
                                        : changeStatus === "down"
                                          ? "text-blue-500"
                                          : "text-gray-400"
                                }`}
                            >
                                {changeStatus === "up" && "▲ "}
                                {changeStatus === "down" && "▼ "}
                                {changeStatus === "neutral" && "- "}
                                {Math.abs(priceChange).toFixed(3)}
                                <span className="ml-1 opacity-80">
                                    ({priceChange > 0 ? "+" : ""}
                                    {priceChangePercent.toFixed(2)}%)
                                </span>
                            </div>
                        )}
                        {/**<div className="text-sm font-medium text-gray-600 bg-gray-200 inline-block px-2 py-0.5 rounded">
                            역대 최고:{" "}
                            <span className="font-bold text-gray-900">
                                {allTimeHigh.toFixed(3)}
                            </span>
                        </div> */}
                    </div>
                </div>
            )}
            {/* 지표 탭 */}
            <div className="flex flex-wrap gap-2">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setSelectedMetric(tab.id)}
                        className={`px-4 py-1.5 rounded-lg font-bold text-sm transition-all duration-200 ${
                            selectedMetric === tab.id
                                ? "bg-gray-900 text-white shadow-md scale-105"
                                : "bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-900"
                        }`}
                    >
                        {tab.name}
                    </button>
                ))}
            </div>

            {/* 차트 렌더링 영역 */}
            <div className="w-full h-[400px] md:h-[500px]">
                {hasData ? (
                    <ReactApexChart
                        key={`chart-axis-${hideEmptyDates ? "category" : "datetime"}`}
                        options={options}
                        series={chartSeries}
                        type="line"
                        height="100%"
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 font-medium">
                        데이터가 없습니다.
                    </div>
                )}
            </div>
            {/* 💡 봉 선택 토글*/}
            <div className="flex flex-col justify-between gap-4 mt-6 border-b border-gray-200 pb-4">
                <div className="flex flex-row gap-4 items-center">
                    {/* 일봉 / 주봉 / 월봉 토글 버튼 */}
                    <div className="flex bg-gray-100 p-1 rounded-lg">
                        <button
                            onClick={() => setTimeframe("daily")}
                            className={`px-3 py-1.5 rounded-md font-bold text-sm transition-all ${
                                timeframe === "daily"
                                    ? "bg-white text-gray-900 shadow"
                                    : "text-gray-500 hover:text-gray-900"
                            }`}
                        >
                            일
                        </button>
                        <button
                            onClick={() => setTimeframe("weekly")}
                            className={`px-3 py-1.5 rounded-md font-bold text-sm transition-all ${
                                timeframe === "weekly"
                                    ? "bg-white text-gray-900 shadow"
                                    : "text-gray-500 hover:text-gray-900"
                            }`}
                        >
                            주
                        </button>
                        <button
                            onClick={() => setTimeframe("monthly")}
                            className={`px-3 py-1.5 rounded-md font-bold text-sm transition-all ${
                                timeframe === "monthly"
                                    ? "bg-white text-gray-900 shadow"
                                    : "text-gray-500 hover:text-gray-900"
                            }`}
                        >
                            월
                        </button>
                    </div>

                    {/* 💡 공백 날짜 무시 토글 스위치 */}
                    <label className="flex items-center gap-2 cursor-pointer bg-gray-50 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors">
                        <input
                            type="checkbox"
                            checked={hideEmptyDates}
                            onChange={(e) =>
                                setHideEmptyDates(e.target.checked)
                            }
                            className="w-4 h-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900 focus:ring-2 cursor-pointer"
                        />
                        <span className="text-sm font-bold text-gray-700 select-none">
                            공백 날짜 무시
                        </span>
                    </label>
                </div>
                {/* Y축 상하한선 입력 폼 */}
                <div className="flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-lg border border-gray-200">
                    <span className="text-sm font-bold text-gray-700 whitespace-nowrap">
                        Y축:
                    </span>
                    <input
                        type="number"
                        step="0.010"
                        value={yMin}
                        onChange={(e) => setYMin(e.target.value)}
                        placeholder="하한"
                        className="w-16 text-center text-sm font-medium border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none p-1"
                    />
                    <span className="text-gray-400 font-bold">-</span>
                    <input
                        type="number"
                        step="0.010"
                        value={yMax}
                        onChange={(e) => setYMax(e.target.value)}
                        placeholder="상한"
                        className="w-16 text-center text-sm font-medium border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none p-1"
                    />
                </div>
            </div>
        </div>
    );
}
